import { IStatusCodeMessage } from "@/models";
import { IMobileVerifyOrSend } from "@/models/onboarding";
import {
  IFindDoctorFilterConfig,
  IFindOrderFilterConfig,
} from "@/models/ApiRequestResponse/patient";
import {
  IDoctorScheduleConsultationType,
  IDocumentTypeEnum,
  IPatientAppointmentStatus,
  IPDashboardPatientAdmitStatus,
  IPrescriptionAttributeType,
  IRoomType,
  ISaveTypePrescription,
} from "@/models/doctor";

export interface ScheduleSlotBufferConfig {
  min: number;
  max: number;
}
export interface IBufferSlotDuration {
  schedule_slot_config: ScheduleSlotBufferConfig;
  schedule_buffer_slots_config: ScheduleSlotBufferConfig;
}

export interface InClinicVideoPaymentMethod {
  frontendText: string;
  id: number;
}

export interface ICheckoutPaymentMethod {
  inClinicPaymentMethod?: InClinicVideoPaymentMethod[];
  videosConsultationPaymentMethod?: InClinicVideoPaymentMethod[];
}

export type IConfigDetail =
  | IBufferSlotDuration
  | IFindDoctorFilterConfig
  | IFindOrderFilterConfig
  | ICheckoutPaymentMethod;
export interface IConfigResponseInfo {
  result: IConfigDetail;
}

export type IConfigResponse = IStatusCodeMessage & IConfigResponseInfo;

export interface ISendOtpPatientCreationResponseInfo {
  result: IMobileVerifyOrSend;
}

export type ISendOtpPatientCreationResponse = IStatusCodeMessage &
  ISendOtpPatientCreationResponseInfo;
export interface IMember {
  id: string;
  user_id: string;
  name: string;
  age: number;
  gender: string;
  is_deleted: boolean;
  is_default: boolean;
  is_self_member: boolean;
  created_at: string;
  updated_at: string;
  createdAt: string;
  updatedAt: string;
}

export interface ICheckMobileNumberDetail {
  userId: string;
  name: string;
  countryCode: string;
  phoneNumber: string;
  age: string | null;
  gender: string | null;
  members: IMember[];
}

export interface ICheckNumberAppointmentCreation {
  result: ICheckMobileNumberDetail;
}

export type ICheckNumberAppointment = IStatusCodeMessage &
  ICheckNumberAppointmentCreation;

export interface IVideoConsultationSlot {
  slotId: string;
  slotStartTime: string;
  slotEndTime: string;
  isBooked: boolean;
  slot_time: number;
  consultationFee: number;
}

export interface IClinicConsultationSlot {
  slotId: string;
  slotStartTime: string;
  slotEndTime: string;
  isBooked: boolean;
  slot_time: number;
  consultationFee: number;
  availableSlots: number;
  clinicName: string;
  clinicCity: string;
  clinicId: string;
  clinicAddress: string;
  latitude: number;
  longitude: number;
}
export interface IVideoAppointment {
  date: string;
  day: string;
  slots: IVideoConsultationSlot[];
}

export interface IClinicAppointment {
  date: string;
  day: string;
  slots: IClinicConsultationSlot[];
}

export interface ISlot {
  clinicId: string;
  clinicName: string;
  clinicCity: string;
  clinicAddress: string;
  latitude: number;
  longitude: number;
  consultationFee: number;
  slots: IClinicConsultationSlot[];
}

export interface IClinicAppointmentSegment {
  date: string;
  day: string;
  slots: ISlot[];
}

export interface IDoctorScheduleSlotDetail {
  videoAppointment: IVideoAppointment[];
  clinicAppointment: IClinicAppointment[];
}

export interface IDoctorScheduleSlot {
  result: IDoctorScheduleSlotDetail;
}

export type IDoctorScheduleSlots = IStatusCodeMessage & IDoctorScheduleSlot;

export interface IPatient {
  name: string;
  age: number;
  records: number;
  symptoms?: string;
  memberId: string;
  patientId: string;
  gender: string;
  phoneNumber?: string;
}
export enum IPatientAppointmentPaymentStatus {
  PAID = "PAID",
  UNPAID = "UNPAID",
  REFUNDED = "REFUNDED",
}
export interface IAppointments {
  id: string;
  slotNo: number;
  patient: IPatient;
  status: IPatientAppointmentStatus;
  slotStartTime: string;
  slotDuration: number;
  activePrescription: boolean;
  clinic_id?: string;
  paymentMethod: string;
  paymentMethodId?: number;
  consultationFee: number;
  discountAmount: number;
  finalAmount?: number;
  paymentStatus: IPatientAppointmentPaymentStatus;
  weekday?: string;
  type_of_consultation?: IDoctorScheduleConsultationType;
  date?: string;
  start_time: string;
  end_time: string;
  clinicName?: string;
  appointmentDate?: string;
  blockedPatient: boolean;
  callerUserId?: string;
  scheduleId?: string;
}

export interface ISchedule {
  id: string;
  type_of_consultation?: IDoctorScheduleConsultationType;
  start_and_end_time: IStartAndEndTime[];
  slot_duration?: number;
  buffer_between_slots?: number;
  consultation_fee?: number;
  slots?: number;
  appointments: IAppointments[];
  clinic_id?: string;
  clinic_name?: string;
}

export interface IScheduleList {
  day: string;
  date: string;
  schedules: ISchedule[];
}

export interface IScheduleListingDetail {
  result: IScheduleList[];
}

export type IScheduleListing = IStatusCodeMessage & IScheduleListingDetail;

export interface ISendOtpPatientCreationRequest {
  phoneNumber: string;
  countryCode: string;
}
export interface IClinicList {
  id: string;
  name: string;
  consultationFee: number;
  latitude: number;
  longitude: number;
}
export interface IClinicListResponseDetail {
  result: IClinicList[];
}

export type IClinicListResponse = IStatusCodeMessage &
  IClinicListResponseDetail;
export interface ISendOTPParams {
  countryCode: string;
  mobileNo: string;
}

export interface ICreatePatientVerifyOtpRequest {
  otp: string;
  otpId: string;
  language: string;
}

export interface ICreatePatientVerifyOtpDetail {
  result: ICheckMobileNumberDetail;
}

export type ICreatePatientVerifyOtpResponse = IStatusCodeMessage &
  ICreatePatientVerifyOtpDetail;

export interface ICancelScheduleAppointments {
  date: string;
  scheduleIds: string[];
}

export interface ICancelScheduleAppointmentsRequest {
  schedules: ICancelScheduleAppointments[];
  appointments: string[];
}

export interface ICancelScheduleAppointmentsResInfo {
  result: boolean;
}
export type ICancelScheduleAppointmentsResponse = IStatusCodeMessage &
  ICancelScheduleAppointmentsResInfo;

export interface IDoctorReview {
  id: string;
  patient_id: string;
  doctor_id: string;
  booking_id: string;
  no_of_stars: number;
  doctor_reply: any;
  review_message: string;
  created_at: string;
  updated_at: string;
  doctor_reply_time: any;
  createdAt: string;
  updatedAt: string;
  name: string;
}

export interface IDoctorReviewRatingDetail {
  review: IDoctorReview[];
  count: number;
  doctorName: string;
}

export interface IDoctorReviewRating {
  result: IDoctorReviewRatingDetail;
}

export type IDoctorReviewRatingResponse = IStatusCodeMessage &
  IDoctorReviewRating;

export interface IReviewReplyRequest {
  reviewId: string;
  reply: string;
}

export interface IReviewReplyInfo {
  result: any;
}

export type IReviewReplyResponse = IStatusCodeMessage & IReviewReplyInfo;

export interface IRescheduleAppointmentRequest {
  id: string;
  slotId: string;
  paymentAmount: number;
}

export interface IRescheduleAppointmentDetail {
  bookingId: string;
  slotNo: number;
}

export interface IRescheduleAppointmentResult {
  result: IRescheduleAppointmentDetail;
}

export type IRescheduleAppointmentResponse = IStatusCodeMessage &
  IRescheduleAppointmentResult;

export interface IHealthCategory {
  id: string;
  name: string;
  icon: string;
}
export enum INextVisitType {
  Days = "Days",
  Weeks = "Weeks",
  Months = "Months",
}
export interface IPrescription {
  nextVisitType: INextVisitType[];
  mealType: string;
  mealTypeTime: string;
  frequency: string;
  frequencyDaysDefault: string;
  healthCategory: IHealthCategory[];
}

export interface IPrescriptionConfig {
  prescription: IPrescription;
}

export interface IPrescriptionConfigDetail {
  result: IPrescriptionConfig;
}

export type IPrescriptionConfigResponse = IStatusCodeMessage &
  IPrescriptionConfigDetail;

export interface ITest {
  id?: string;
  testId: string;
  name: string;
  note: string;
}

export interface IMedicine {
  id?: string;
  medicineId: string;
  name: string;
  mealType: string;
  mealTypeTime: string;
  whenText: string;
  frequency: string;
  frequencyDays: number;
  frequencyText: string;
  note: string;
}

export interface ITagOtherHealthProblemDiagnosis {
  id: string;
  name: string;
}

export interface IPrescriptionPayload {
  id?: string;
  bookingId?: string;
  bp?: string;
  pulse?: number;
  temperature?: number;
  weight?: number;
  height?: number;
  bmi?: number;
  tag?: ITagOtherHealthProblemDiagnosis[];
  otherHealthProblem?: ITagOtherHealthProblemDiagnosis[];
  complaint?: string[];
  diagnosis?: ITagOtherHealthProblemDiagnosis[];
  medicine?: IMedicine[];
  test?: ITest[];
  dietAndExercise?: string;
  note?: string;
  nextVisitType?: INextVisitType;
  nextVisitNo?: number;
  healthCategoryId?: string;
  prescription_id?: string;
  saveType?: ISaveTypePrescription;
  doctorId?: string;
  patientId?: string;
  memberId?: string;
}

export interface ICreatePrescriptionResult {
  id: string;
  url?: string;
}
export interface ICreatePrescriptionResponseDetail {
  result: ICreatePrescriptionResult;
}

export type ICreatePrescriptionResponse = IStatusCodeMessage &
  ICreatePrescriptionResponseDetail;

export interface IMedicineTest {
  id: string;
  name: string;
  description: string;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IMedicineTestDetail {
  result: IMedicineTest[];
}
export type IMedicineTestResponse = IStatusCodeMessage & IMedicineTestDetail;

export interface IHealthCategory {
  id: string;
  name: string;
  icon: string;
}

export interface IMedicalRecord {
  id: string;
  patientId: string;
  memberId: string;
  doctorId: any;
  doctorName?: string | null;
  type: string;
  url: string;
  documentDate: string;
  thumbnailUrl: string;
  healthCategory: IHealthCategory;
  uploaderBy: string;
  uploaderByUserId: string;
}

export interface IMedicalRecordDetail {
  records: IMedicalRecord[];
  count: number;
  activePrescription: IPrescriptionPayload;
}

export interface IMedicalRecordResult {
  result: IMedicalRecordDetail;
}

export type IMedicalRecordResponse = IStatusCodeMessage & IMedicalRecordResult;

export interface ISaveMedicalRecordRequest {
  bookingId?: string;
  url: string;
  thumbnailUrl?: string;
  healthCategoryId: string;
  memberId?: string;
  documentDate?: string;
  type?: IDocumentTypeEnum;
}

export interface ISaveMedicalRecordResult {
  id: string;
  url: string;
}

export interface ISaveMedicalRecordDetail {
  result: ISaveMedicalRecordResult;
}

export type ISaveMedicalRecordResponse = IStatusCodeMessage &
  ISaveMedicalRecordDetail;

export interface IUploadFileToS3ResultDetail {
  url: string;
  thumbnailUrl: string;
}

export interface IUploadFileToS3Result {
  result: IUploadFileToS3ResultDetail;
}
export type IUploadFileToS3Response = IStatusCodeMessage &
  IUploadFileToS3Result;

export interface ISearchPrescriptionAttributesDetail {
  id: string;
  name: string;
  type: IPrescriptionAttributeType;
}

export interface ISearchPrescriptionAttributes {
  result: ISearchPrescriptionAttributesDetail[];
}

export type ISearchPrescriptionAttributesResponse = IStatusCodeMessage &
  ISearchPrescriptionAttributes;

export interface IAppointmentDetailInfo {
  result: IAppointments[];
}

export type ISearchedAppointmentResponse = IStatusCodeMessage &
  IAppointmentDetailInfo;

export interface IPrescriptionTemplateList {
  id: string;
  doctorId: string;
  name: string;
  diagnosis: ITagOtherHealthProblemDiagnosis[];
  dietAndExercise: string;
  medicine: IMedicine[];
  test: ITest[];
}

export interface IPrescriptionTemplateListDetails {
  result: IPrescriptionTemplateList[];
}

export type IPrescriptionTemplateListResponse = IStatusCodeMessage &
  IPrescriptionTemplateListDetails;

export interface IDeletePrescriotionTemplate {
  result: boolean;
}

export type IDeletePrescriotionTemplateResponse = IStatusCodeMessage &
  IDeletePrescriotionTemplate;

export interface IPrescriptionTemplateCreateUpdatePayload {
  name: string;
  diagnosis: ITagOtherHealthProblemDiagnosis[];
  medicine: IMedicine[];
  test: ITest[];
  dietAndExercise: string;
}

export interface IPrescriptionTemplateCreateUpdateDetail {
  result: string;
}

export interface IPrescriptionTemplateValue {
  id: string;
  doctorId: string;
  name: string;
  diagnosis: ITagOtherHealthProblemDiagnosis[];
  medicine: IMedicine[];
  test: ITest[];
  dietAndExercise: string;
}

export type IPrescriptionTemplateCreateUpdateResponse = IStatusCodeMessage &
  IPrescriptionTemplateCreateUpdateDetail;

export interface IDoctorHospitalDetail {
  hospital_id: string;
  name: string;
  sponsors: string;
}
export interface IDoctorHospitalList {
  result: IDoctorHospitalDetail[];
}

export type IDoctorHospitalListResponse = IStatusCodeMessage &
  IDoctorHospitalList;

export interface IHospitalPatient {
  id: string;
  doctor_id: string;
  patient_id: string;
  member_id: string;
  hospital_id: string;
  hospital_room_id: string;
  room_bed_id: string;
  sponsor_name: string;
  case_history: string;
  admission_date: string;
  hospital_uhid: string;
  status: IPDashboardPatientAdmitStatus;
  created_at: string;
  updated_at: string;
  name: string;
  number: number;
  room_name: string;
  type: IRoomType;
  discharged_date: string;
}

export interface IHospitalPatientResponse {
  data: IHospitalPatient[];
  count: number;
  page?: number;
  hasMore?: boolean;
}
export interface IHospitalPatientDetail {
  result: IHospitalPatientResponse;
}
export type IHospitalPatientListResponse = IStatusCodeMessage &
  IHospitalPatientDetail;

export interface IBed {
  id: string;
  number: number;
}
export interface IHospitalRooms {
  id: string;
  name: string;
  type: IRoomType;
  bedBookedCount: number;
  countryCode: string;
  phoneNumber: string;
  beds: IBed[];
  bedCount?: number;
}

export interface IHospitalRoomsDetail {
  result: IHospitalRooms[];
}

export type IHospitalRoomsResponse = IStatusCodeMessage & IHospitalRoomsDetail;

export interface IAdmitPatientRequest {
  doctor_id: string;
  patient_id: string;
  member_id: string;
  hospital_id: string;
  hospital_room_id: string;
  room_bed_id: string;
  sponsor_name: string;
  case_history: string;
  admission_date: string;
  hospital_uhid: string;
  name: string;
  age: number;
  gender: string;
}

export interface IAdmitPatientDetail {
  result: string;
}

export type IAdmitPatientResponse = IStatusCodeMessage & IAdmitPatientDetail;

export interface IAdmittedPatientDetail {
  id: string;
  doctor_id: string;
  patient_id: string;
  member_id: string;
  hospital_id: string;
  hospital_room_id: string;
  room_bed_id: string;
  sponsor_name: string;
  case_history: string;
  admission_date: string;
  hospital_uhid: string;
  status: IPDashboardPatientAdmitStatus;
  discharged_date: string;
  created_at: string;
  updated_at: string;
  name: string;
  phone_number: string;
  age: number;
  gender: number;
  number: number;
  room_available: boolean;
  room_name: string;
  type: string;
  is_readmitted: boolean;
  countryCode?: string;
  hospital_name?: string;
  room_phone_number?: string;
  hospital_image?: string;
  bed_number?: number;
  patient_details?: IAdmittedPatientDetail;
  room_phone?: string;
}

export interface IAdmittedPatientInfo {
  result: IAdmittedPatientDetail;
}

export type IAdmittedPatientResponse = IStatusCodeMessage &
  IAdmittedPatientInfo;

export interface IUpdateAdmitPatientRequest {
  id: string;
  hospital_room_id?: string;
  room_bed_id?: string;
  status?: IPDashboardPatientAdmitStatus;
  member_id?: string;
  hospital_id?: string;
  sponsor_name?: string;
  case_history?: string;
  admission_date?: string;
  hospital_uhid?: string;
}

export interface IUpdateAdmitPatientDetail {
  result: any;
}

export type IUpdateAdmitPatientResponse = IStatusCodeMessage &
  IUpdateAdmitPatientDetail;
export interface IActivePrescriptionDetail {
  result: IPrescriptionPayload;
}

export type IActivePrescriptionResponse = IStatusCodeMessage &
  IActivePrescriptionDetail;

export interface IAgoraRTCToken {
  result: string;
}

export interface IAgoraRTMToken {
  rtmToken: string;
  uid: string;
}

export type IAgoraRTCTokenResponse = IStatusCodeMessage & IAgoraRTCToken;
export type IAgoraRTMTokenResponse = IStatusCodeMessage & IAgoraRTMToken;

export interface IDoctorAssociatedClinicMemberProfile {
  userId: string;
  name: string;
  countryCode: string;
  country_code?: string;
  phoneNumber: string;
  phone_number?: string;
  role: string;
  profilePhoto?: string;
  age?: number;
  gender?: string;
  photo_url?: string;
}

export interface IDoctorAssociatedClinicMemberProfileInfo {
  result: IDoctorAssociatedClinicMemberProfile[];
}
export type IDoctorAssociatedClinicMemberProfileResponse = IStatusCodeMessage &
  IDoctorAssociatedClinicMemberProfileInfo;

export interface ISearchMemberInfo {
  result: IDoctorAssociatedClinicMemberProfile;
}
export type ISearchMemberResponse = IStatusCodeMessage & ISearchMemberInfo;

export enum ICallSource {
  APP_CALL = "APP_CALL",
  IP_CALL = "IP_CALL",
}
export interface IPushCallNotificationPayload {
  callingUserId: string;
  callingUserName: string;
  callerName: string;
  bookingId: string;
  experience?: string;
  qualification?: string;
  callerId?: string;
  speciality?: string;
  type?: ICallType;
  uuid?: string;
  memberId?: string;
  doctorName?: string;
  patientName?: string;
  patientId?: string;
  callSource?: ICallSource;
}
export enum ICallType {
  CALL = "CALL",
  REJECTED = "REJECTED",
}

export interface IPushCallNotificationInfo {
  result: string;
}

export type IPushCallNotificationResponse = IStatusCodeMessage &
  IPushCallNotificationInfo;

export interface IHelpSupportRequest {
  problem: string;
  description: string;
  attachments: string[];
}

export interface IHelpSupport {
  id: string;
  status: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  problem: string;
  description: string;
  attachments: string[];
  user_id: string;
  role: string;
}

export interface IHelpSupportDetail {
  result: IHelpSupport;
}

export type IHelpSupportResponse = IStatusCodeMessage & IHelpSupportDetail;

export interface IDoctorVideoReview {
  id: string;
  doctor_id: string;
  video_link: string;
  heading: string;
  description: string;
  language: string;
  createdAt: string;
  updatedAt: string;
}

export interface IDoctorVideoReviewsResponse {
  result: IDoctorVideoReview[];
}

export interface IReceivePaymentResult {
  bookingId: string;
}

export interface IReceivePaymentResponseDetail {
  result: IReceivePaymentResult;
}

export type IReceivePaymentResponse = IStatusCodeMessage &
  IReceivePaymentResponseDetail;

export interface IReceivePaymentRequest {
  bookingId: string;
  finalAmount: number;
  paymentMethodId: number;
}

export interface IGetBillDetailResponse {
  url: string;
  thumbnailUrl: string;
}

export interface IGetBillResponseDetail {
  result: IGetBillDetailResponse;
}

export type IGetBillResponse = IStatusCodeMessage & IGetBillResponseDetail;

export interface IBlockUnblockUserRequest {
  patientId: string;
  isBlock: boolean;
}

export interface IBlockUnblockUserResponseDetail {
  result: boolean;
}

export type IBlockUnblockUserResponse = IStatusCodeMessage &
  IBlockUnblockUserResponseDetail;

export interface DetailItemProps {
  icon: string;
  text: string | number;
}

export interface PaidPaymentDetailsProps {
  finalAmount: number;
  discountAmount: number;
  consultationFee?: number;
}

export interface PatientDetailItem {
  icon: string;
  text: string;
}

export interface AppointmentDetailItem {
  icon: string;
  text: string;
}

export enum IPaymentMethod {
  CASH = "Cash",
  UPI = "UPI",
}

export enum IPaymentMethodType {
  CASH = 1,
  ONLINE = 2,
  LATER = 3,
}

export interface MakeBillState {
  fee: number;
  isBillSharedOpen: boolean;
  isBillOpenLoading: boolean;
  isBlockUnBlockConfirm: boolean;
  isChangePaymentMethodOpen: boolean;
  paymentMethod: IPaymentMethod;
  isPaymentConfirmLoading: boolean;
  isPaymentRefundLoading: boolean;
  refundAmount: number;
  isMakeRefundOpen: boolean;
  isQRCodeOpen: boolean;
}

export enum MakeBillActionType {
  SET_FEE = "SET_FEE",
  SET_BILL_SHARED_OPEN = "SET_BILL_SHARED_OPEN",
  SET_BILL_OPEN_LOADING = "SET_BILL_OPEN_LOADING",
  SET_BLOCK_UNBLOCK_CONFIRM = "SET_BLOCK_UNBLOCK_CONFIRM",
  SET_CHANGE_PAYMENT_METHOD_OPEN = "SET_CHANGE_PAYMENT_METHOD_OPEN",
  SET_PAYMENT_METHOD = "SET_PAYMENT_METHOD",
  SET_PAYMENT_LOADING = "SET_PAYMENT_LOADING",
  SET_PAYMENT_REFUND_LOADING = "SET_PAYMENT_REFUND_LOADING",
  SET_REFUND_AMOUNT = "SET_REFUND_AMOUNT",
  SET_MAKE_REFUND_OPEN = "SET_MAKE_REFUND_OPEN",
  SET_QR_CODE_OPEN = "SET_QR_CODE_OPEN",
}

export type MakeBillAction =
  | { type: MakeBillActionType.SET_FEE; payload: number }
  | { type: MakeBillActionType.SET_BILL_SHARED_OPEN; payload: boolean }
  | { type: MakeBillActionType.SET_BILL_OPEN_LOADING; payload: boolean }
  | { type: MakeBillActionType.SET_BLOCK_UNBLOCK_CONFIRM; payload: boolean }
  | {
      type: MakeBillActionType.SET_CHANGE_PAYMENT_METHOD_OPEN;
      payload: boolean;
    }
  | { type: MakeBillActionType.SET_PAYMENT_METHOD; payload: IPaymentMethod }
  | { type: MakeBillActionType.SET_PAYMENT_LOADING; payload: boolean }
  | { type: MakeBillActionType.SET_PAYMENT_REFUND_LOADING; payload: boolean }
  | { type: MakeBillActionType.SET_REFUND_AMOUNT; payload: number }
  | { type: MakeBillActionType.SET_MAKE_REFUND_OPEN; payload: boolean }
  | { type: MakeBillActionType.SET_QR_CODE_OPEN; payload: boolean };

export interface IAppointmentStatusResult {
  status: IPatientAppointmentStatus;
  activePrescription: boolean;
  slotNo: number;
  consultationType: IDoctorScheduleConsultationType;
  slotStartTime: string;
  slotEndTime: string;
  slotDate: Date;
  doctorName: string;
  speciality: string;
  degree: string;
  experienceYears: number;
}

export interface IAppointmentStatusResponseDetail {
  result: IAppointmentStatusResult;
}

export type IAppointmentStatusResponse = IStatusCodeMessage &
  IAppointmentStatusResponseDetail;

export interface IVideoCallSessionUser {
  name: string;
  userId: string;
  agoraUid: string;
  role: string;
  joinedAt: string;
}

export interface IVideoCallSessionResult {
  session_id: string;
  users: IVideoCallSessionUser[];
}
export interface IVideoCallSessionResponseDetail {
  result: IVideoCallSessionResult;
}

export type IVideoCallSessionResponse = IStatusCodeMessage &
  IVideoCallSessionResponseDetail;
export interface IVideoCallSessionPayload {
  users: IVideoCallSessionUser[];
}

export interface IVideoCallSessionPostResult {
  session_id: string;
}

export interface IVideoCallSessionPostResponseDetail {
  result: IVideoCallSessionPostResult;
}

export type IVideoCallSessionPostResponse = IStatusCodeMessage &
  IVideoCallSessionPostResponseDetail;

export interface IDoctorReviewDeleteResponseDetail {
  result: any;
}

export type IDoctorReviewDeleteResponse = IStatusCodeMessage &
  IDoctorReviewDeleteResponseDetail;

export interface IStartAndEndTime {
  start_time: string;
  end_time: string;
}

export enum ISlotReserveFrom {
  SLOT_RESERVE_FROM_START = 1,
  SLOT_RESERVE_FROM_END = 2,
}

export interface ICreateAvailabilitySettingPayload {
  type_of_consultation: string;
  start_and_end_time: IStartAndEndTime[];
  slot_duration: number;
  buffer_between_slots: number;
  consultation_fee: number;
  schedule_dates: string[];
  clinic_id?: string;
  appointment_display?: number;
  total_slots: number;
  reserved_slots_count?: number;
  slot_reserve_from?: ISlotReserveFrom;
}

export interface ICreateUpdateAvailabilitySettingDetail {
  result: string | boolean;
}

export type ICreateUpdateAvailabilitySettingResponse = IStatusCodeMessage &
  ICreateUpdateAvailabilitySettingDetail;

export interface IAvailabilitySetting {
  id: string;
  type_of_consultation: string;
  clinic_id: string;
  start_and_end_time: IStartAndEndTime[];
  total_slots: number;
  slot_duration: number;
  buffer_between_slots: number;
  schedule_date: string;
  schedule_start_time: string;
  consultation_fee: number;
  reserved_slots_count: number;
  slot_reserve_from: number;
  appointment_display: number;
  blocked_roles: any[];
  booked: number;
  slots: number;
  clinic_name: string;
}

export interface IAvailabilitySettingResult {
  clinic_id: string;
  clinic_name: string;
  type_of_consultation: string;
  schedules: IAvailabilitySetting[];
}

export interface IGetAvailabilitySettingResponseDetail {
  result: IAvailabilitySettingResult[];
}

export type IGetAvailabilitySettingResponse = IStatusCodeMessage &
  IGetAvailabilitySettingResponseDetail;

export interface IDeleteAvailabilitySettingResponseDetail {
  result: boolean;
}

export type IDeleteAvailabilitySettingResponse = IStatusCodeMessage &
  IDeleteAvailabilitySettingResponseDetail;

export interface IUpdateAvailabilitySettingPayload {
  start_and_end_time: IStartAndEndTime[];
  slot_duration: number;
  buffer_between_slots: number;
  appointment_display?: number;
  total_slots: number;
}

export interface IBlockedBookingAvailabilitySettingPayload {
  block_booking: string[];
}

export interface IBlockedBookingAvailabilitySettingResponseDetail {
  result: boolean;
}

export type IBlockedBookingAvailabilitySettingResponse = IStatusCodeMessage &
  IBlockedBookingAvailabilitySettingResponseDetail;

export enum ICreateEditAvailabilityAction {
  SET_CONSULTATION_TYPE_OPEN = "SET_CONSULTATION_TYPE_OPEN",
  SET_CLINIC_NAME_OPEN = "SET_CLINIC_NAME_OPEN",
  SET_SELECTED_DATES = "SET_SELECTED_DATES",
  SET_SLOT_RESERVE_FROM = "SET_SLOT_RESERVE_FROM",
  SET_START_END_TIME = "SET_START_END_TIME",
  SET_FORM_DATA = "SET_FORM_DATA",
  SET_SAVE_OR_UPDATE_LOADING = "SET_SAVE_OR_UPDATE_LOADING",
}

export type ICreateEditAvailabilityActionType =
  | {
      type: ICreateEditAvailabilityAction.SET_CONSULTATION_TYPE_OPEN;
      payload: boolean;
    }
  | {
      type: ICreateEditAvailabilityAction.SET_CLINIC_NAME_OPEN;
      payload: boolean;
    }
  | {
      type: ICreateEditAvailabilityAction.SET_SLOT_RESERVE_FROM;
      payload: boolean;
    }
  | {
      type: ICreateEditAvailabilityAction.SET_SELECTED_DATES;
      payload: string[];
    }
  | {
      type: ICreateEditAvailabilityAction.SET_START_END_TIME;
      payload: boolean;
    }
  | {
      type: ICreateEditAvailabilityAction.SET_SAVE_OR_UPDATE_LOADING;
      payload: boolean;
    }
  | {
      type: ICreateEditAvailabilityAction.SET_FORM_DATA;
      payload: Partial<ICreateEditAvailabilityState["formData"]>;
    };

export interface ICreateEditAvailabilityState {
  consultationTypeOrClinicSelectionOpen: {
    consultationType: boolean;
    clinicName: boolean;
  };
  slotReserveFromOpen: boolean;
  startEndTimeSelectionOpen: boolean;
  selectedDates: string[];
  formData: {
    consultationType: string;
    clinicId: string;
    clinicName: string;
    startTime: string;
    endTime: string;
    duration: string;
    consultationFee: string;
    bufferDuration: string;
    slotDuration: string;
    totalSlots: string;
    appointmentDisplay: string;
    reserveSlots: string;
    slotReserveFrom: string;
    startAndEndTime: IStartAndEndTime[];
  };
  saveOrUpdateLoading: boolean;
}

export interface IHospitalTeam {
  userId: string;
  profileUrl: string;
  name: string;
  countryCode: string;
  phoneNumber: string;
  role: string;
}

export interface IHospitalTeamDetail {
  result: IHospitalTeam[];
}

export type IHospitalTeamResponse = IStatusCodeMessage & IHospitalTeamDetail;

export interface IRefundPaymentPayload {
  bookingId: string;
  amount: number;
}

export interface IRefundPaymentDetail {
  status: number;
  msg: string;
  request_id: string;
  bank_ref_num: any;
  mihpayid: number;
  error_code: number;
}

export interface IRefundPaymentInfo {
  result: IRefundPaymentDetail;
}

export type IRefundPaymentResponse = IStatusCodeMessage & IRefundPaymentInfo;
